import { useState, useEffect } from 'react';
import {
  withCachedPromiseResult,
  withCachedValueResult,
  buildUniqueKey,
  isCached,
  removeCache,
  MAX_TIMEOUT,
} from '@sendpayments/js-utils/dist/services/caching';

// when using the "concurrentRefreshKey" this hook will get the data from the cached and return it.
// If the data was already cached will send
// a refresh on the background and will update the hook with the result. This is so we can pre-load
// the ui with cached data before the new data comes.
const useFetch = (paramsAsObject, doFetch, { initialState, concurrentRefreshKey } = { initialState: {} }) => {
  let isFetchCanceled = false;

  const [data, setData] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [isBackgroundRefresh, setBackgroundRefresh] = useState(false);
  const [failed, setFailed] = useState(false);

  // this is so we do not leak call to setState if the effect was removed. (the await may be in flight while the hook is removed)
  const callIfNotCancelled = (f) => !isFetchCanceled && f();

  const backgroundRefresh = async (doRefresh) => {
    const fetchedRate = await doRefresh();
    callIfNotCancelled(() => setData(fetchedRate));
  };

  const awaitConcurrentFetch = async ({ useLoading } = { useLoading: true }) => {
    const uniqueKey = buildUniqueKey(concurrentRefreshKey, paramsAsObject);
    const cache = withCachedPromiseResult(uniqueKey, { timeout: MAX_TIMEOUT });
    const cacheValue = withCachedValueResult(uniqueKey, { timeout: MAX_TIMEOUT });
    const wasCached = isCached(uniqueKey);

    try {
      setLoading(!wasCached && useLoading);

      const fetchedResult = await cache(() => doFetch(paramsAsObject));
      setData(fetchedResult);

      if (wasCached && !isFetchCanceled) {
        callIfNotCancelled(() => setBackgroundRefresh(true));

        backgroundRefresh(async () => {
          try {
            const result = await doFetch(paramsAsObject);
            removeCache(uniqueKey);
            callIfNotCancelled(() => setBackgroundRefresh(false));
            return cacheValue(() => result);
          } catch {
            removeCache(uniqueKey);

            callIfNotCancelled(() => {
              setData(initialState);
              setFailed(true);
              setBackgroundRefresh(false);
            });
            return undefined;
          }
        });
      }

      callIfNotCancelled(() => {
        setLoading(false);
        setFailed(false);
      });
    } catch {
      if (wasCached) {
        removeCache(uniqueKey);
      }

      callIfNotCancelled(() => {
        setLoading(false);
        setFailed(true);
        setData(initialState);
      });
    }
  };

  const awaitFetch = async ({ useLoading } = { useLoading: true }) => {
    try {
      setLoading(useLoading);

      const fetchedResult = await doFetch(paramsAsObject);

      callIfNotCancelled(() => {
        setData(fetchedResult);
        setLoading(false);
        setFailed(false);
      });
    } catch {
      callIfNotCancelled(() => {
        setLoading(false);
        setFailed(true);
        setData(initialState);
      });
    }
  };

  useEffect(
    () => {
      if (!isFetchCanceled) {
        if (concurrentRefreshKey) {
          awaitConcurrentFetch();
        } else {
          awaitFetch();
        }
      }

      return () => {
        isFetchCanceled = true;
      };
    },
    paramsAsObject ? [...Object.values(paramsAsObject)] : [],
  );

  const invalidateCache = () => {
    if (concurrentRefreshKey) {
      const uniqueKey = buildUniqueKey(concurrentRefreshKey, paramsAsObject);
      removeCache(uniqueKey);
    }
  };

  const reFetch = (...args) => {
    if (concurrentRefreshKey) {
      invalidateCache();
      return awaitConcurrentFetch(...args);
    }

    return awaitFetch(...args);
  };

  return [{ data, loading, failed, isBackgroundRefresh }, reFetch, invalidateCache];
};

export default useFetch;
